import { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit3, Loader2, Plus, RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useAppSelector } from '@/app/hooks';
import { useToast } from '@/hooks/use-toast';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import {
  createAdminPaperType,
  fetchAdminPaperTypes,
  updateAdminPaperType,
  type AdminPaperType,
  type AdminPaperTypeInput,
} from '@/services/adminService';

const PAPER_CATEGORIES = ['FINEART', 'STANDARD', 'ECONOMY'] as const;
const MEDIA_TYPES = ['PAPER', 'CANVAS'] as const;

type FormState = {
  name: string;
  displayCode: string;
  brand: string;
  category: (typeof PAPER_CATEGORIES)[number];
  mediaType: (typeof MEDIA_TYPES)[number];
  weightGsm: string;
  widthIn: string;
  lengthIn: string;
  isRoll: boolean;
  available: boolean;
  description: string;
};

const buildFormState = (paper?: AdminPaperType): FormState => ({
  name: paper?.name ?? '',
  displayCode: paper?.displayCode ?? '',
  brand: paper?.brand ?? '',
  category: paper?.category ?? 'FINEART',
  mediaType: paper?.mediaType ?? 'PAPER',
  weightGsm: paper?.weightGsm != null ? String(paper.weightGsm) : '',
  widthIn: paper?.widthIn != null ? String(paper.widthIn) : '',
  lengthIn: paper?.lengthIn != null ? String(paper.lengthIn) : '',
  isRoll: paper?.isRoll ?? false,
  available: paper?.available ?? true,
  description: paper?.description ?? '',
});

const filterPaperTypes = (paperTypes: ReadonlyArray<AdminPaperType>, query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return paperTypes;
  }
  return paperTypes.filter((paper) => [
    paper.name,
    paper.displayCode ?? '',
    paper.brand,
    paper.category,
    paper.mediaType,
    paper.available ? 'available' : 'inactive',
  ].join(' ').toLowerCase().includes(normalized));
};

const buildPayload = (form: FormState): AdminPaperTypeInput => ({
  name: form.name.trim(),
  displayCode: form.displayCode.trim() || null,
  brand: form.brand.trim(),
  category: form.category,
  mediaType: form.mediaType,
  weightGsm: Number(form.weightGsm),
  widthIn: Number(form.widthIn),
  lengthIn: Number(form.lengthIn),
  isRoll: form.isRoll,
  available: form.available,
  description: form.description.trim() || null,
});

const AdminPaperTypesPage = () => {
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paperTypes, setPaperTypes] = useState<ReadonlyArray<AdminPaperType>>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'saving' | 'error'>('idle');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(() => buildFormState());

  const loadPaperTypes = useCallback(async () => {
    try {
      setStatus('loading');
      const next = await fetchAdminPaperTypes();
      setPaperTypes(next);
      setStatus('idle');
    } catch (error) {
      logging.error('Failed to load admin paper types', { error });
      setStatus('error');
      toast({ title: 'Failed to load paper types', variant: 'destructive' });
    }
  }, [toast]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    void loadPaperTypes();
  }, [isAdmin, loadPaperTypes]);

  const filtered = useMemo(() => filterPaperTypes(paperTypes, search), [paperTypes, search]);
  const isEditing = editingId != null;

  const resetForm = useCallback(() => {
    setEditingId(null);
    setForm(buildFormState());
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setStatus('saving');
      const payload = buildPayload(form);
      const saved = editingId
        ? await updateAdminPaperType(editingId, payload)
        : await createAdminPaperType(payload);
      setPaperTypes((current) => {
        if (!editingId) {
          return [saved, ...current].sort((a, b) => Number(b.available) - Number(a.available) || a.name.localeCompare(b.name));
        }
        return current.map((entry) => entry.id === saved.id ? saved : entry).sort((a, b) => Number(b.available) - Number(a.available) || a.name.localeCompare(b.name));
      });
      analytics.track('button: ui.admin_paper_types_save', { paperTypeId: saved.id, mode: editingId ? 'edit' : 'create' });
      toast({ title: editingId ? 'Paper type updated' : 'Paper type created' });
      resetForm();
      setStatus('idle');
    } catch (error) {
      logging.error('Failed to save admin paper type', { error, editingId });
      setStatus('error');
      toast({ title: 'Failed to save paper type', description: error instanceof Error ? error.message : undefined, variant: 'destructive' });
    }
  }, [editingId, form, resetForm, toast]);

  const handleEdit = useCallback((paper: AdminPaperType) => {
    setEditingId(paper.id);
    setForm(buildFormState(paper));
  }, []);

  const handleToggleAvailability = useCallback(async (paper: AdminPaperType) => {
    try {
      const updated = await updateAdminPaperType(paper.id, { available: !paper.available });
      setPaperTypes((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
      toast({ title: updated.available ? 'Paper type enabled' : 'Paper type disabled' });
    } catch (error) {
      logging.error('Failed to toggle paper type availability', { error, paperTypeId: paper.id });
      toast({ title: 'Failed to update availability', variant: 'destructive' });
    }
  }, [toast]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Admin access required</CardTitle>
            <CardDescription>You do not have permission to view this page.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')}>Return home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-10">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Paper types</CardTitle>
              <CardDescription>Manage paper inventory, display codes, and availability used by exports and order setup.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => void loadPaperTypes()} disabled={status === 'loading' || status === 'saving'}>
                {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                Refresh
              </Button>
              <Button onClick={() => { resetForm(); analytics.track('button: ui.admin_paper_types_new'); }}>
                <Plus className="mr-2 h-4 w-4" />
                New paper
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{isEditing ? 'Edit paper type' : 'Create paper type'}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="paper-name">Name</Label>
            <Input id="paper-name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="paper-display-code">Display code</Label>
            <Input id="paper-display-code" value={form.displayCode} onChange={(event) => setForm((current) => ({ ...current, displayCode: event.target.value }))} placeholder="PhotoRag" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="paper-brand">Brand</Label>
            <Input id="paper-brand" value={form.brand} onChange={(event) => setForm((current) => ({ ...current, brand: event.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(value) => setForm((current) => ({ ...current, category: value as FormState['category'] }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PAPER_CATEGORIES.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Media type</Label>
            <Select value={form.mediaType} onValueChange={(value) => setForm((current) => ({ ...current, mediaType: value as FormState['mediaType'] }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{MEDIA_TYPES.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="paper-gsm">Weight GSM</Label>
            <Input id="paper-gsm" type="number" value={form.weightGsm} onChange={(event) => setForm((current) => ({ ...current, weightGsm: event.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="paper-width">Width in</Label>
            <Input id="paper-width" type="number" value={form.widthIn} onChange={(event) => setForm((current) => ({ ...current, widthIn: event.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="paper-length">Length in</Label>
            <Input id="paper-length" type="number" value={form.lengthIn} onChange={(event) => setForm((current) => ({ ...current, lengthIn: event.target.value }))} />
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <div className="text-sm font-medium">Roll paper</div>
              <div className="text-xs text-muted-foreground">Turn on for roll media.</div>
            </div>
            <Switch checked={form.isRoll} onCheckedChange={(checked) => setForm((current) => ({ ...current, isRoll: checked }))} />
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <div className="text-sm font-medium">Available</div>
              <div className="text-xs text-muted-foreground">Available papers appear in ordering tools.</div>
            </div>
            <Switch checked={form.available} onCheckedChange={(checked) => setForm((current) => ({ ...current, available: checked }))} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="paper-description">Description</Label>
            <Textarea id="paper-description" rows={3} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
          </div>
          <div className="flex gap-2 md:col-span-2">
            <Button onClick={() => void handleSubmit()} disabled={status === 'saving'}>
              {status === 'saving' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isEditing ? 'Save changes' : 'Create paper'}
            </Button>
            <Button variant="outline" onClick={resetForm} disabled={status === 'saving'}>Cancel</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Inventory</CardTitle>
              <CardDescription>{paperTypes.length} paper type{paperTypes.length === 1 ? '' : 's'}</CardDescription>
            </div>
            <Input className="max-w-sm" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search papers…" />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Display code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Media</TableHead>
                <TableHead>GSM</TableHead>
                <TableHead>Sheet</TableHead>
                <TableHead>Roll</TableHead>
                <TableHead>Available</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((paper) => (
                <TableRow key={paper.id}>
                  <TableCell className="font-medium">{paper.displayCode ?? '—'}</TableCell>
                  <TableCell>{paper.name}</TableCell>
                  <TableCell>{paper.brand}</TableCell>
                  <TableCell>{paper.category}</TableCell>
                  <TableCell>{paper.mediaType}</TableCell>
                  <TableCell>{paper.weightGsm}</TableCell>
                  <TableCell>{paper.widthIn} x {paper.lengthIn}</TableCell>
                  <TableCell>{paper.isRoll ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{paper.available ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(paper)}>
                        <Edit3 className="mr-2 h-4 w-4" />Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => void handleToggleAvailability(paper)}>
                        {paper.available ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPaperTypesPage;
